const PCM_WORKLET_MODULE_URL = new URL('./pcm-downsampler-processor.js', import.meta.url)
const PCM_WORKLET_PROCESSOR_NAME = 'pcm-downsampler-processor'

/** Callbacks used while a {@link PcmAudioTap} is delivering PCM chunks. */
export interface PcmAudioTapHandlers {
  /** Called with each ~60ms 16kHz mono 16-bit little-endian PCM chunk. */
  onData: (chunk: ArrayBuffer) => void
  /** Called if the worklet processor throws at runtime. */
  onError: () => void
}

export interface PcmAudioTap {
  /** Stops delivering PCM chunks and releases the worklet node + source node. */
  dispose(): void
}

/**
 * Taps a MediaStream through an AudioWorklet that resamples it to 16kHz mono
 * and delivers little-endian 16-bit PCM chunks via `handlers.onData`. Used in
 * place of MediaRecorder's compressed WebM/Opus output for the live STT
 * socket, which expects raw PCM.
 *
 * @param audioContext AudioContext driving the tap; the worklet module is
 *   registered on it lazily (safe to call more than once per context).
 * @param stream Source MediaStream to capture (mic-only or already-mixed).
 * @param handlers Chunk/error callbacks.
 * @returns A handle used to stop tapping the stream.
 */
export async function createPcmAudioTap(
  audioContext: AudioContext,
  stream: MediaStream,
  handlers: PcmAudioTapHandlers
): Promise<PcmAudioTap> {
  await audioContext.audioWorklet.addModule(PCM_WORKLET_MODULE_URL)

  const sourceNode = audioContext.createMediaStreamSource(stream)
  const workletNode = new AudioWorkletNode(audioContext, PCM_WORKLET_PROCESSOR_NAME)

  workletNode.port.onmessage = (event: MessageEvent<ArrayBuffer>) => handlers.onData(event.data)
  workletNode.onprocessorerror = () => handlers.onError()

  sourceNode.connect(workletNode)

  return {
    dispose(): void {
      workletNode.port.onmessage = null
      workletNode.onprocessorerror = null
      try {
        sourceNode.disconnect(workletNode)
      } catch {
        /* ignore disconnect errors during teardown */
      }
      workletNode.disconnect()
    },
  }
}
